/**
 * Save to S3 (`s3.sink`) inspector: connection, file format, bucket, folder
 * browse, filename.
 *
 * The **table** half of Save to Blob with S3 vocabulary. There are no Veeva
 * data kinds here: those fetch each record's documents into a folder tree, and
 * the platform's own landing zone for that is Blob.
 *
 * There is also no "create a bucket" affordance, because the connector offers
 * no bucket lifecycle at all (ADR-0021 §5) — the destination bucket is one the
 * customer already owns.
 */

import { Anchor, Button, Group, Loader, Select, Stack, Text, TextInput } from "@mantine/core";
import { subfoldersOf, useS3Browse } from "./useS3Browse";
import { ConnectionRefSelect } from "./ConnectionRefSelect";
import { useConnectionRef } from "./useConnectionRef";

const FORMATS = [
  { value: "csv", label: "CSV" },
  { value: "tsv", label: "TSV" },
  { value: "json", label: "JSON" },
  { value: "jsonl", label: "JSONL" },
  { value: "xlsx", label: "Excel (.xlsx)" },
];

const DELIMITERS = [
  { value: ",", label: "Comma  ," },
  { value: ";", label: "Semicolon  ;" },
  { value: "\t", label: "Tab" },
  { value: "|", label: "Pipe  |" },
];

export function S3SinkConfig({
  config,
  editable,
  onChange,
}: {
  config: Record<string, unknown>;
  editable: boolean;
  onChange: (key: string, value: unknown) => void;
}) {
  // A node names a connection id or a `@role`; everything here takes an id.
  const {
    connectionId,
    role,
    settled: roleSettled,
  } = useConnectionRef(String(config.connectionId ?? ""));
  const bucket = String(config.bucket ?? "");
  const prefix = String(config.prefix ?? "");
  const filename = String(config.filename ?? "");
  const format = String(config.format ?? "csv");
  const { connections, buckets, objects, loading } = useS3Browse(
    connectionId,
    bucket,
    prefix || null,
  );
  const folders = subfoldersOf(objects, prefix);
  // The browser leaves a trailing slash, but a prefix typed or imported into
  // the config need not have one — and the engine appends it either way, so
  // the preview has to as well or it shows a key the run will not write.
  const folderPrefix = prefix && !prefix.endsWith("/") ? `${prefix}/` : prefix;
  const segments = prefix ? prefix.replace(/\/$/, "").split("/") : [];

  return (
    <Stack gap="xs">
      <ConnectionRefSelect
        label="S3 connection"
        placeholder={connections.length ? "Pick a connection" : "No S3 connections set up"}
        data={connections}
        nothingFoundMessage="No S3 connections"
        connectionId={connectionId}
        role={role}
        settled={roleSettled}
        editable={editable}
        onChange={(v) => {
          const name = connections.find((c) => c.value === v)?.label ?? "";
          onChange("connectionId", v);
          onChange("connectionName", name);
          onChange("bucket", "");
          onChange("prefix", "");
        }}
      />

      <Select
        label="File format"
        allowDeselect={false}
        disabled={!editable}
        value={format}
        data={FORMATS}
        onChange={(v) => onChange("format", v ?? "csv")}
      />

      {format === "csv" && (
        <Select
          label="Delimiter"
          allowDeselect={false}
          disabled={!editable}
          value={String(config.delimiter ?? "") || ","}
          data={DELIMITERS}
          onChange={(v) => onChange("delimiter", v ?? ",")}
        />
      )}

      {connectionId && (
        <Select
          label="Bucket"
          placeholder="Pick a bucket"
          data={buckets}
          value={bucket || null}
          disabled={!editable}
          searchable
          onChange={(v) => {
            onChange("bucket", v ?? "");
            onChange("prefix", "");
          }}
        />
      )}

      {connectionId && bucket && (
        <Stack gap={4}>
          <Text size="xs" c="dimmed">
            Folder
          </Text>
          <Group gap={4} wrap="wrap">
            <Anchor size="xs" onClick={() => editable && onChange("prefix", "")}>
              {bucket}
            </Anchor>
            {segments.map((seg, i) => (
              <Group key={i} gap={4} wrap="nowrap">
                <Text size="xs" c="dimmed">
                  /
                </Text>
                <Anchor
                  size="xs"
                  onClick={() =>
                    editable && onChange("prefix", segments.slice(0, i + 1).join("/") + "/")
                  }
                >
                  {seg}
                </Anchor>
              </Group>
            ))}
          </Group>
          {loading ? (
            <Loader size="xs" />
          ) : folders.length > 0 ? (
            <Stack gap={2}>
              {folders.map((f) => (
                <Button
                  key={f}
                  variant="subtle"
                  size="compact-xs"
                  justify="flex-start"
                  disabled={!editable}
                  onClick={() => onChange("prefix", `${folderPrefix}${f}/`)}
                >
                  📁 {f}
                </Button>
              ))}
            </Stack>
          ) : (
            <Text size="xs" c="dimmed">
              No subfolders here — the file will be saved in this folder.
            </Text>
          )}
        </Stack>
      )}

      {connectionId && bucket && (
        <TextInput
          label="File name"
          placeholder={`e.g. output.${format === "xlsx" ? "xlsx" : format}`}
          value={filename}
          disabled={!editable}
          onChange={(e) => onChange("filename", e.currentTarget.value)}
        />
      )}

      {connectionId && bucket && filename && (
        <Text size="xs" mt={4}>
          Saving to:{" "}
          <Text span ff="monospace">
            {bucket}/{folderPrefix}
            {filename}
          </Text>
        </Text>
      )}
    </Stack>
  );
}
